import * as Sentry from '@sentry/browser';
import { useCallback } from 'react';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { authenticatePhone } from '../api/authenticatePhone';
import { Metrics } from '../constants';
import { cannotAttemptAuthenticateChallenge } from '../utils/cannotAttemptAuthenticateChallenge';
import { formatAuthResponse } from '../utils/formatAuthResponse';

/**
 * @description submits a 6-digit verification code challenge to authenticate a user's phone with Twilio Verify
 * @param {AUTH_PHONE_RESPONSE_BODY|null} apiResponseBody
 * @param {string} email
 * @param {string} errorMessage
 * @param {function} setIsAuthSuccessful
 * @param {Object} isLockedUser - useRef object, .current is boolean
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {function(shouldClearErrorMessageOnUpdate: boolean): void} setShouldClearErrorMessageOnUpdate - removes a blocker from clearing the error message on update
 * @param {function(errorMessage: string): void} setErrorMessage - sets error message displayed to user
 * @param {function(shouldDisableForm: boolean): void} setShouldDisableButton
 * @param {function(shouldDisableForm: boolean): void} setShouldDisableForm - disables the verification form if the session expires or the user makes too many attempts
 * @param {function(verificationCode: string): void} setVerificationCode - sets error message displayed to user
 * @param {string} verificationId
 * @param {string} verificationCode
 * @param {string} verificationToken
 *
 * @returns {function(*): void}
 */
export function usePhoneAuthentication({
    apiResponseBody,
    email,
    errorMessage,
    setIsAuthSuccessful,
    isLockedUser,
    sendMetric,
    setShouldDisableButton,
    setShouldClearErrorMessageOnUpdate,
    setErrorMessage,
    setShouldDisableForm,
    setVerificationCode,
    verificationId,
    verificationCode,
    verificationToken,
}) {
    return useCallback(
        (e) => {
            e.preventDefault();

            async function sendCodeForAuthentication() {
                sendMetric({
                    action: Metrics.ClickContinue,
                    element: 'MFAContinueButton',
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.CLICK,
                });

                setShouldDisableButton(true);

                try {
                    const response = await authenticatePhone({
                        email,
                        verificationId,
                        verificationToken,
                        verificationCode,
                    });

                    if (!response) {
                        sendMetric({
                            action: Metrics.ViewErrorMessage,
                            element: 'ErrorMessage',
                            initiator: EVENT_INITIATORS.USER,
                            interactionType: INTERACTION_TYPES.VIEW,
                            eventDetails: {
                                error: 'Unhandled error message.',
                                hook: 'usePhoneAuthentication',
                            },
                        });

                        setShouldDisableButton(false);
                        setErrorMessage('There was a problem logging you in.');
                        setShouldClearErrorMessageOnUpdate(true);
                        setVerificationCode('');

                        return;
                    }
                    // successful response returns redirectUrl & entityId
                    if (response?.success) {
                        apiResponseBody.current = formatAuthResponse(
                            response.body
                        );

                        setIsAuthSuccessful(true);
                        setErrorMessage('');
                        setShouldClearErrorMessageOnUpdate(true);
                    } else {
                        sendMetric({
                            action: Metrics.ViewErrorMessage,
                            element: 'ErrorMessage',
                            initiator: EVENT_INITIATORS.USER,
                            interactionType: INTERACTION_TYPES.VIEW,
                            eventDetails: {
                                error:
                                    response?.errorMessage ||
                                    'There was a problem logging you in.',
                                hook: 'usePhoneAuthentication',
                            },
                        });

                        if (response?.body?.redirectUrl) {
                            apiResponseBody.current = response.body;
                            isLockedUser.current = true;
                        }

                        setErrorMessage(
                            response?.errorMessage ||
                                'There was a problem logging you in.'
                        );
                        setShouldClearErrorMessageOnUpdate(true);
                        setVerificationCode('');

                        setShouldDisableForm(
                            cannotAttemptAuthenticateChallenge(
                                response?.errorCode
                            )
                        );
                        setShouldDisableButton(false);
                    }
                } catch (err) {
                    sendMetric({
                        action: Metrics.ViewErrorMessage,
                        element: 'ErrorMessage',
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.VIEW,
                        eventDetails: {
                            error: err,
                            hook: 'usePhoneAuthentication',
                        },
                    });

                    Sentry.captureException(err);
                    setErrorMessage('There was a problem logging you in.');
                    setShouldClearErrorMessageOnUpdate(true);
                    setVerificationCode('');
                    setShouldDisableButton(false);
                }
            }

            sendCodeForAuthentication();
        },
        [
            sendMetric,
            setShouldDisableButton,
            email,
            verificationId,
            verificationToken,
            verificationCode,
            setErrorMessage,
            setShouldClearErrorMessageOnUpdate,
            setVerificationCode,
            apiResponseBody,
            setIsAuthSuccessful,
            setShouldDisableForm,
            isLockedUser,
        ]
    );
}
